import React from "react";
import ReactDOM from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { buildClinicVerifyUrl } from "../../../shared/utils/publicVerifyUrl";

interface QRDoorSignPrintProps {
  /**
   * Approved clinic id. The clinic is the stable public identity — it outlives
   * the registration/onboarding request that created it, so a printed sign keeps
   * working after that request is archived.
   */
  clinicId: string;
  clinicName?: string | null;
  doctorName?: string | null;
  licenseNumber?: string | null;
}

const QRDoorSignPrint: React.FC<QRDoorSignPrintProps> = ({
  clinicId,
  clinicName,
  doctorName,
  licenseNumber,
}) => {
  const { t } = useLanguage();

  const qrCodeUrl = buildClinicVerifyUrl(clinicId);

  return ReactDOM.createPortal(
    <div
      className="qr-door-sign-print hidden"
      style={{
        padding: "0",
        backgroundColor: "white",
        fontFamily: "Arial, sans-serif",
        direction: "ltr",
      }}
    >
      <style>
        {`
          @media print {
            #root {
              display: none !important;
            }
            .qr-door-sign-print {
              display: block !important;
            }
            @page {
              size: A4;
              margin: 0;
            }
          }
        `}
      </style>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          padding: "20px",
          textAlign: "center",
          boxSizing: "border-box",
        }}
      >
        {/* Logos */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "40px",
            marginBottom: "24px",
          }}
        >
          <img
            src="/ima_logo.png"
            alt="IMA Logo"
            style={{ width: "120px", height: "auto", objectFit: "contain" }}
          />
          <img
            src="/health360_logo.png"
            alt="Health360 Logo"
            style={{ width: "120px", height: "auto", objectFit: "contain" }}
          />
        </div>

        {/* Clinic / doctor identity, so the sign is meaningful without scanning */}
        {(clinicName || doctorName || licenseNumber) && (
          <div style={{ marginBottom: "20px", maxWidth: "560px" }} dir="auto">
            {clinicName && (
              <h1
                style={{
                  fontSize: "28px",
                  color: "#1f2937",
                  fontWeight: "bold",
                  margin: "0 0 8px 0",
                  lineHeight: "1.4",
                }}
              >
                {clinicName}
              </h1>
            )}
            {doctorName && (
              <p style={{ fontSize: "20px", color: "#374151", margin: "0 0 4px 0" }}>
                {doctorName}
              </p>
            )}
            {licenseNumber && (
              <p style={{ fontSize: "18px", color: "#4b5563", margin: "0" }}>
                {t("license_number")} {licenseNumber}
              </p>
            )}
          </div>
        )}

        {/* QR Code */}
        <div
          style={{
            border: "6px solid #2E5D7D",
            borderRadius: "16px",
            padding: "24px",
            backgroundColor: "#ffffff",
            marginBottom: "16px",
          }}
        >
          <QRCodeCanvas
            value={qrCodeUrl}
            size={300}
            level="H"
            includeMargin={true}
          />
        </div>

        <p style={{ fontSize: "18px", color: "#2E5D7D", margin: "0 0 24px 0", fontWeight: 600 }}>
          {t("scan_to_verify")}
        </p>

        {/* Bottom Text */}
        <div
          style={{
            textAlign: "center",
            borderTop: "3px solid #2E5D7D",
            paddingTop: "20px",
            maxWidth: "500px",
          }}
        >
          <h2
            style={{
              fontSize: "26px",
              color: "#2E5D7D",
              fontWeight: "bold",
              margin: "0 0 10px 0",
              lineHeight: "1.4",
            }}
          >
            {t("iraqi_medical_association")}
          </h2>
          <h3
            style={{
              fontSize: "22px",
              color: "#555",
              fontWeight: "600",
              margin: "0",
              lineHeight: "1.4",
            }}
          >
            {t("clinic_documentation_platform")}
          </h3>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QRDoorSignPrint;
